import { useState } from 'react';
import { useConnection, useReadContract } from 'wagmi';
import { erc20Abi, liquidityLockAbi, uniswapV2RouterAbi } from '@syntra/abi';
import { Link } from '../app/router.tsx';
import { ActionButton } from '../components/tx/ActionButton.tsx';
import { CountUp } from '../components/ui/motion.tsx';
import { AmountInput, Card, Notice, Rows, SectionHead, Segmented } from '../components/ui/primitives.tsx';
import { Donut } from '../components/ui/viz.tsx';
import { DEPLOYMENT as D, EXPLORER } from '../config/env.ts';
import { useChainNow } from '../hooks/useChainClock.tsx';
import { usePool } from '../hooks/usePool.ts';
import { usePosition } from '../hooks/usePosition.ts';
import { useI18n } from '../i18n/index.tsx';
import { amountsForLp, lpForDeposit, matchAmount, withSlippage } from '../lib/amm.ts';
import { fmtPct, fmtToken, fmtUsd, parseAmount, toNum, toInput, fmtBalance } from '../lib/format.ts';
import { PoolStacks } from '../components/ui/coins.tsx';

const SLIPPAGE = 100n; // 1%

export function Liquidity() {
  const { t } = useI18n();
  const [mode, setMode] = useState<'add' | 'remove'>('add');
  const { address } = useConnection();
  const { data: me } = usePosition(address);
  const pool = usePool();
  const share = me.lp !== undefined && pool.supply ? Number((me.lp * 1_000_000n) / pool.supply) / 1_000_000 : 0;
  const [uG, uU] = me.lp !== undefined && pool.rSyntra !== undefined && pool.rUsdt !== undefined && pool.supply ? amountsForLp(me.lp, pool.rSyntra, pool.rUsdt, pool.supply) : [undefined, undefined];
  return (
    <>
      <SectionHead
        eyebrow={t('liq.eyebrow')}
        title={t('liq.title')}
        right={
          <Segmented<'add' | 'remove'>
            label="mode"
            value={mode}
            onChange={setMode}
            options={[
              { value: 'add', label: t('liq.add') },
              { value: 'remove', label: t('liq.remove') },
            ]}
          />
        }
      />
      <div className="grid two liq-layout">
        {mode === 'add' ? <AddForm /> : <RemoveForm />}
        <div className="grid">
          <Ownership mine={me.lp} />
          <Card title={t('liq.position')}>
            {!address ? (
              <p className="soft">{t('common.connectFirst')}</p>
            ) : (
              <Rows
                items={[
                  [t('liq.lpBalance'), fmtToken(me.lp, 6)],
                  [t('liq.poolShare'), fmtPct(share, 4)],
                  [t('liq.underlying'), `${fmtToken(uG)} SYN · ${fmtToken(uU)} USDT`],
                ]}
              />
            )}
            <p className="soft" style={{ fontSize: 13 }}>
              {t('liq.bondIt')} <Link to="/bond">{t('liq.goBond')}</Link>
            </p>
            <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
              {t('liq.risk')}
            </p>
          </Card>
        </div>
      </div>
    </>
  );
}

/** The pool's rate and the slippage the router call allows, under both forms. */
function poolRows(t: ReturnType<typeof useI18n>['t'], pool: ReturnType<typeof usePool>): [string, string][] {
  const rate = pool.rSyntra && pool.rUsdt !== undefined ? fmtToken((pool.rUsdt * 10n ** 18n) / pool.rSyntra, 4) : undefined;
  return [
    [t('swap.rate'), rate !== undefined ? `1 SYN = ${rate} USDT` : '—'],
    [t('liq.slippage'), fmtPct(Number(SLIPPAGE) / 10_000, 0)],
  ];
}

const NOWHERE = '0x0000000000000000000000000000000000000000';

/**
 * Who owns the pool's LP supply: the initial liquidity in its 12-month lock (D3), the treasury (protocol-owned
 * liquidity), the connected wallet, and everyone else.
 */
function Ownership({ mine }: { mine: bigint | undefined }) {
  const { t } = useI18n();
  const pool = usePool();
  const pol = useReadContract({ address: D.syntraUsdtPair, abi: erc20Abi, functionName: 'balanceOf', args: [D.treasury], query: { refetchInterval: 15_000 } });
  const lockQ = { enabled: Boolean(D.lpLock), refetchInterval: 60_000 };
  const lockedQ = useReadContract({ address: D.lpLock ?? NOWHERE, abi: liquidityLockAbi, functionName: 'locked', query: lockQ });
  const unlockQ = useReadContract({ address: D.lpLock ?? NOWHERE, abi: liquidityLockAbi, functionName: 'unlockAt', query: lockQ });
  const supply = pool.supply;
  const treasury = pol.data as bigint | undefined;
  const locked = D.lpLock ? (lockedQ.data as bigint | undefined) : 0n;
  const unlockAt = unlockQ.data !== undefined ? Number(unlockQ.data) : undefined;
  const you = mine ?? 0n;
  const others = supply !== undefined && treasury !== undefined && locked !== undefined ? supply - treasury - locked - you : undefined;
  const pct = (v: bigint | undefined) => (supply && v !== undefined ? fmtPct(Number((v * 1_000_000n) / supply) / 1_000_000, 2) : '—');
  const slices = [
    ...(D.lpLock ? [{ key: 'locked', label: t('liq.locked'), value: toNum(locked), display: pct(locked), color: 'var(--chart-4)' }] : []),
    { key: 'pol', label: t('liq.treasury'), value: toNum(treasury), display: pct(treasury), color: 'var(--chart-1)' },
    { key: 'you', label: t('liq.you'), value: toNum(you), display: pct(you), color: 'var(--chart-2)' },
    { key: 'others', label: t('liq.others'), value: toNum(others), display: pct(others), color: 'var(--chart-3)' },
  ];
  // connected: your share; otherwise the locked initial liquidity (or, without a lock, the protocol-owned share)
  const focus = mine !== undefined ? you : D.lpLock ? locked : treasury;
  const centre = supply && focus !== undefined ? Number((focus * 10_000n) / supply) / 100 : undefined;
  return (
    <Card className="ownership">
      <h3>{t('liq.ownership')}</h3>
      <Donut
        slices={slices}
        label={t('liq.ownership')}
        center={
          <>
            <b className="num">
              <CountUp value={centre} format={(v) => `${v < 10 && v > 0 ? v.toFixed(1) : Math.round(v)}%`} />
            </b>
            <span>{mine !== undefined ? t('liq.yourShare') : D.lpLock ? t('liq.lockedShort') : t('liq.polShort')}</span>
          </>
        }
      />
      <div className="stat-pair">
        <div>
          <div className="k">{t('swap.tvl')}</div>
          <div className="v num">{pool.rUsdt !== undefined ? fmtUsd(2 * toNum(pool.rUsdt)) : '—'}</div>
          <div className="s">{t('liq.tvlSub')}</div>
        </div>
        <div>
          <div className="k">{t('swap.reserves')}</div>
          {/* at the pool's own price both sides are worth the same, so the stacks stand level */}
          <PoolStacks
            size={20}
            sides={[
              { token: 'SYN', usd: pool.rSyntra !== undefined && pool.rUsdt !== undefined ? toNum(pool.rUsdt) : NaN, label: `${fmtToken(pool.rSyntra, 0)} SYN` },
              { token: 'USDT', usd: pool.rUsdt !== undefined ? toNum(pool.rUsdt) : NaN, label: `${fmtToken(pool.rUsdt, 0)} USDT` },
            ]}
          />
        </div>
      </div>
      {D.lpLock && unlockAt !== undefined ? (
        <p className="soft lp-lock-note" data-testid="lp-lock">
          {t('liq.lockedUntil', { pct: pct(locked), date: new Date(unlockAt * 1000).toLocaleDateString() })}{' '}
          {EXPLORER ? (
            <a href={`${EXPLORER}/address/${D.lpLock}`} target="_blank" rel="noreferrer">
              {t('liq.lockContract')}
            </a>
          ) : null}
        </p>
      ) : null}
    </Card>
  );
}

function AddForm() {
  const { t } = useI18n();
  const { address } = useConnection();
  const { data: me } = usePosition(address);
  const pool = usePool();
  const now = useChainNow(30_000);
  const [gInput, setG] = useState('');
  const [uInput, setU] = useState('');
  const g = parseAmount(gInput);
  const u = parseAmount(uInput);
  const ready = pool.rSyntra !== undefined && pool.rUsdt !== undefined && pool.supply !== undefined;
  const onSyntra = (v: string) => {
    setG(v);
    const a = parseAmount(v);
    if (a !== null && ready) setU(a === 0n ? '' : toInput(matchAmount(a, pool.rSyntra!, pool.rUsdt!)));
  };
  const onUsdt = (v: string) => {
    setU(v);
    const a = parseAmount(v);
    if (a !== null && ready) setG(a === 0n ? '' : toInput(matchAmount(a, pool.rUsdt!, pool.rSyntra!)));
  };
  const lp = g && u && ready ? lpForDeposit(g, u, pool.rSyntra!, pool.rUsdt!, pool.supply!) : 0n;
  const shareAfter = ready && lp > 0n ? Number(((lp + (me.lp ?? 0n)) * 1_000_000n) / (pool.supply! + lp)) / 1_000_000 : 0;
  const blocked =
    !g || !u
      ? t('common.enterAmount')
      : me.syntra !== undefined && g > me.syntra
        ? t('common.insufficient', { token: 'SYN' })
        : me.usdt !== undefined && u > me.usdt
          ? t('common.insufficient', { token: 'USDT' })
          : null;
  return (
    <Card title={t('liq.ctaAdd')} highlight>
      <p className="soft" style={{ marginTop: 0 }}>
        {t('liq.first')}
      </p>
      <div className="field" style={{ gap: 12 }}>
        <AmountInput id="liq-syntra" label="SYN" value={gInput} onChange={onSyntra} token="SYN" icon="/token-syntra.png" balance={fmtBalance(me.syntra)} />
        <AmountInput id="liq-usdt" label="USDT" value={uInput} onChange={onUsdt} token="USDT" icon="/token-usdt.svg" balance={fmtBalance(me.usdt)} />
        <Rows
          items={[
            ...poolRows(t, pool),
            [t('liq.lpOut'), fmtToken(lp, 6)],
            [t('liq.share'), fmtPct(shareAfter, 4)],
          ]}
        />
        <ActionButton
          approve={[
            ...(g ? [{ token: D.syntra, spender: D.router, amount: g, symbol: 'SYN' }] : []),
            ...(u ? [{ token: D.usdt, spender: D.router, amount: u, symbol: 'USDT' }] : []),
          ]}
          label={t('liq.ctaAdd')}
          blocked={blocked}
          request={
            g && u && address
              ? {
                  address: D.router,
                  abi: uniswapV2RouterAbi,
                  functionName: 'addLiquidity',
                  args: [D.syntra, D.usdt, g, u, withSlippage(g, SLIPPAGE), withSlippage(u, SLIPPAGE), address, BigInt(Math.floor(now + 1200))],
                }
              : null
          }
          onDone={() => {
            setG('');
            setU('');
          }}
        />
      </div>
    </Card>
  );
}

function RemoveForm() {
  const { t } = useI18n();
  const { address } = useConnection();
  const { data: me } = usePosition(address);
  const pool = usePool();
  const now = useChainNow(30_000);
  const [input, setInput] = useState('');
  const lp = parseAmount(input);
  const ready = pool.rSyntra !== undefined && pool.rUsdt !== undefined && pool.supply;
  const [outG, outU] = lp && ready ? amountsForLp(lp, pool.rSyntra!, pool.rUsdt!, pool.supply!) : [0n, 0n];
  const blocked = !lp ? t('common.enterAmount') : me.lp !== undefined && lp > me.lp ? t('common.insufficient', { token: 'LP' }) : null;
  return (
    <Card title={t('liq.ctaRemove')} highlight>
      <div className="field" style={{ gap: 12 }}>
        <AmountInput
          id="liq-remove"
          label={t('liq.removeAmount')}
          value={input}
          onChange={setInput}
          token="LP"
          icon="/token-lp.svg"
          balance={fmtBalance(me.lp, 6)}
          onMax={me.lp !== undefined ? () => setInput(toInput(me.lp!)) : undefined}
        />
        <div className="segmented">
          {[25, 50, 75, 100].map((pct) => (
            <button key={pct} type="button" aria-pressed={false} onClick={() => me.lp !== undefined && setInput(toInput((me.lp * BigInt(pct)) / 100n))}>
              {pct}%
            </button>
          ))}
        </div>
        <Rows items={[...poolRows(t, pool), [t('liq.youGet'), `${fmtToken(outG)} SYN · ${fmtToken(outU)} USDT`]]} />
        {me.lp === 0n ? <Notice>{t('liq.noLp')}</Notice> : null}
        <ActionButton
          label={t('liq.ctaRemove')}
          blocked={blocked}
          approve={lp ? { token: D.syntraUsdtPair, spender: D.router, amount: lp, symbol: 'LP' } : undefined}
          request={
            lp && address
              ? {
                  address: D.router,
                  abi: uniswapV2RouterAbi,
                  functionName: 'removeLiquidity',
                  args: [D.syntra, D.usdt, lp, withSlippage(outG, SLIPPAGE), withSlippage(outU, SLIPPAGE), address, BigInt(Math.floor(now + 1200))],
                }
              : null
          }
          onDone={() => setInput('')}
        />
      </div>
    </Card>
  );
}
